/**
 * What this page will use for its picture, and what happened when it looked.
 *
 * ⚠ THE POLICY IS CHANS'S, 2026-08-21, AND IT IS AN ORDER OF PREFERENCE, NOT
 * A BAN. The earlier reading — that stock imagery was forbidden — came from
 * the PMax creative matrix and was Viktor's rule for that folder ("that's
 * Victor doing that... it can be disregarded"). Asked directly whether stock
 * and AI are acceptable on landing pages, the answer was yes, with a priority
 * attached: "Any time that we can use real authentic photos from the client,
 * that's what we'd wanna use... but if we don't have that option, then we
 * don't have that option, and we need to generate."
 *
 * ⚠ HE ASKED FOR THIS TEXT SPECIFICALLY. "I think that we need to state that:
 * hey look, priority here is an image of this service going on this page.
 * Please upload it. If you're not able to upload a photo, we can generate one
 * using AI." The generator already obeyed that order — client pool first,
 * then stock, then AI — but nothing said it out loud to the person building
 * the page, and nothing showed them what it had tried.
 *
 * ⚠ THE PLAN WAS RETURNED BY THE BACKEND AND READ BY NOTHING. `media_plan`
 * was missing from LandingPreviewResponse, so no surface could show why a
 * page shipped bare. Measured on agileandco/"SEO Services": stock returned
 * three photographs and the subject gate rejected all three, AI generated one
 * and it was rejected too, and the only visible outcome was a page with no
 * picture and no stated reason.
 *
 * The photographable control itself is NOT repeated here — it already exists
 * in the builder form. This points at it when the evidence says it is the
 * answer, rather than offering a second copy that could disagree with it.
 */
import { Camera, Info } from 'lucide-react';

import { PhotoUpload } from '@/components/landing/PhotoUpload';
import type { LandingMediaPlan } from '@/services/landingPagesService';

interface Props {
  tenantId: string;
  serviceName: string;
  /** null = unstated. Read only, to decide whether to suggest stating it. */
  photographable: boolean | null;
  /** Absent or empty means nothing was sourced — usually the GOOD case: the
   *  client's own photograph was used and no fallback was needed. */
  plan?: LandingMediaPlan;
  onUploaded?: () => void;
}

/** Plain words for a tier result. `stock:all_rejected` says nothing to a human. */
const TIER_WORDS: Record<string, string> = {
  'stock:used': 'a stock photo was used',
  'stock:all_rejected': 'stock photos were found, but none showed the service',
  'stock:nothing_found': 'no stock photo matched',
  'ai:used': 'an AI image was generated and used',
  'ai:all_rejected': 'an AI image was generated, but it did not show the service',
  'ai:nothing_found': 'AI generation returned nothing',
};

export function HeroImagery({
  tenantId,
  serviceName,
  photographable,
  plan,
  onUploaded,
}: Props) {
  const tiers = plan?.tiers_attempted ?? [];
  const usedSourced = plan?.outcome === 'used';
  const everythingRejected = plan?.outcome === 'all_rejected_by_the_subject_gate';

  // ⚠ THE TELL THAT THE SERVICE, NOT THE PHOTOGRAPH, IS THE PROBLEM. When
  // every tier is rejected for the same missing word, no picture will ever
  // carry it — "SEO" is not a thing a camera can point at. Sourcing harder is
  // the wrong response; stating the fact in the Photographable field is the
  // right one, and on agileandco it let the client's OWN studio photograph
  // through on the next run with nothing sourced at all.
  const unphotographableSignal =
    everythingRejected && !!plan?.gate_missing && photographable !== false;

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-start gap-2">
        <Camera className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
        <div>
          <h3 className="text-sm font-semibold">Picture for this page</h3>
          <p className="mt-1 text-sm text-teal-dark">
            A real photo of {serviceName.trim() || 'this service'} from the
            client is what we want here. Please upload one. If you cannot get a
            photo, we can generate one — but a real job beats a generated one
            every time.
          </p>
        </div>
      </div>

      <PhotoUpload
        tenantId={tenantId}
        serviceName={serviceName}
        onUploaded={onUploaded}
      />

      {/* Shown only once the generator has actually tried, so it reads as a
          result rather than a setting nobody asked about. */}
      {tiers.length > 0 && (
        <div className="rounded-md bg-teal-light p-3">
          <p className="text-sm font-medium">
            {usedSourced
              ? 'No client photo was usable, so one was sourced'
              : 'No client photo was usable, and nothing sourced worked either'}
          </p>
          <ul className="mt-2 space-y-1 text-sm text-teal-dark">
            {tiers.map((t) => (
              <li key={t}>{TIER_WORDS[t] ?? t}</li>
            ))}
          </ul>
          {plan?.used?.provider && (
            <p className="mt-2 text-xs text-teal-dark">
              Source: {plan.used.provider}
              {plan.used.photographer_name
                ? ` — ${plan.used.photographer_name}`
                : ''}
            </p>
          )}
        </div>
      )}

      {plan?.state === 'declined' && (
        <p className="rounded-md bg-destructive/5 p-3 text-sm">
          This client declined stock and generated imagery, so the page can only
          use their own photographs.
        </p>
      )}

      {unphotographableSignal && (
        <div className="rounded-md border-l-4 border-l-coral bg-coral-light p-3">
          <p className="flex items-start gap-2 text-sm font-medium">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            Every photo was rejected for the same reason
          </p>
          <p className="mt-1 text-sm text-teal-dark">
            None of them showed &ldquo;{plan?.gate_missing}&rdquo;. If that is
            not something a camera can point at — an agency&rsquo;s SEO, a
            24/7 dispatch — then no photograph will ever pass. Set
            Photographable to &ldquo;No&rdquo; above and generate again, and the
            client&rsquo;s own photos of their premises or team can be used
            instead of being refused for not showing a thing that has no
            picture.
          </p>
        </div>
      )}
    </div>
  );
}
